import { Box, Chip, type SxProps, type Theme } from "@mui/material";
import type { ReactNode } from "react";

export type StatusBadgeTone = "highlight" | "active" | "neutral" | "missed";

const toneStyles: Record<
  StatusBadgeTone,
  {
    bgcolor: string;
    color: string;
    borderColor: string;
  }
> = {
  // Used inside a light highlighted surface, so it punches back into the dark UI.
  highlight: {
    bgcolor: "background.default",
    color: "primary.main",
    borderColor: "primary.main",
  },
  active: {
    bgcolor: "primary.main",
    color: "background.default",
    borderColor: "primary.main",
  },
  neutral: {
    bgcolor: "primary.main",
    color: "background.default",
    borderColor: "#0a0a0a",
  },
  missed: {
    bgcolor: "#ff3d00",
    color: "#0a0a0a",
    borderColor: "#0a0a0a",
  },
};

/** A compact, uppercase marker for state and ranking labels throughout the app. */
export const StatusBadge = ({
  label,
  tone = "neutral",
  sx,
}: {
  label: ReactNode;
  tone?: StatusBadgeTone;
  sx?: SxProps<Theme>;
}) => (
  <Box
    component="span"
    sx={{ display: "inline-flex", flexShrink: 0, ml: "1px", mr: "3px", mt: "1px", mb: "3px", ...sx }}
  >
    <Chip
      label={label}
      size="small"
      sx={{
        height: 18,
        borderRadius: 0,
        border: "1px solid",
        fontSize: "0.6rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        transition: "transform 140ms ease, box-shadow 140ms ease",
        "&:hover": { transform: "translate(-1px,-1px)", boxShadow: "3px 3px 0 #ff3d12" },
        "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } },
        "& .MuiChip-label": { px: 0.75, lineHeight: 1 },
        ...toneStyles[tone],
      }}
    />
  </Box>
);
